import React, { useEffect } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { config, fetchCountryCode } from "@/store/slices/config";

import { capitalizeFirstLetter } from "@/utils";
import { defaultCountryCodeAndMobileLength } from "@/utils/Config";

import { ISelectCountryType } from "@/models";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      boxShadow: "none",
      border: "none",
      "& .MuiMenu-list": {
        padding: 0,
      },
      "& .MuiMenuItem-root": {
        borderBottom: "none",
      },
      "@media (max-width: 900px)": {
        mt: -1,
      },
    },
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

interface IProps {
  isSmallScreen: boolean;
  countryCodeAndMobileLength: string;
  setCountryCodeAndMobileLength: (
    countryCode: string,
    mobileLength: string
  ) => void;
  type?: ISelectCountryType;
  disabled?: boolean;
}
const SelectCountry: React.FC<IProps> = (props) => {
  const {
    isSmallScreen,
    countryCodeAndMobileLength,
    setCountryCodeAndMobileLength,
    type,
    disabled = false,
  } = props;

  const { countryCodeList } = useAppSelector(config);

  const dispatch = useAppDispatch();

  const handleChangeCountryCode = (e: SelectChangeEvent) => {
    const [countryCode, mobileLength] = e.target.value.split("_");
    setCountryCodeAndMobileLength(countryCode, mobileLength);
  };

  const validValues = Object.keys(countryCodeList).map(
    (countryName) =>
      `${countryCodeList[countryName]?.code}_${countryCodeList[countryName]?.len}`
  );

  const safeValue = validValues.includes(countryCodeAndMobileLength)
    ? countryCodeAndMobileLength
    : "";

  useEffect(() => {
    dispatch(fetchCountryCode());
    const [countryCode, mobileLength] =
      defaultCountryCodeAndMobileLength.split("_");
    setCountryCodeAndMobileLength(countryCode, mobileLength);
  }, []);

  return (
    <FormControl
      sx={{
        mr: 1,
        width: "30%",
        p: 0,
        height:
          type === ISelectCountryType.MAKE_APPOINTMENT
            ? "-webkit-fill-available"
            : type === ISelectCountryType.MAKE_APPOINTMENT_MOBILE_OR_TABLET
            ? "5.4rem"
            : "auto",
      }}
    >
      <InputLabel id="code-select-label">Code</InputLabel>
      <Select
        disabled={disabled}
        labelId="code-select-label"
        input={<OutlinedInput label="Code" />}
        value={safeValue}
        onChange={handleChangeCountryCode}
        MenuProps={{
          ...MenuProps,
          ...(isSmallScreen
            ? {
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
              }
            : {
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }),
        }}
        sx={{
          "& .MuiSelect-select": {
            padding: "0.8rem",
            fontSize: "1.4rem",
            display: "flex",
            flexDirection: "column",
            color: countryCodeAndMobileLength ? "inherit" : "var(--gray-400)",
          },
          "& .MuiSelect-select:focus": {
            border: "none",
          },
        }}
        fullWidth
      >
        {Object.keys(countryCodeList)?.map((countryName, index) => (
          <MenuItem
            key={`${countryName}-${index}`}
            value={`${countryCodeList[countryName]?.code}_${countryCodeList[countryName]?.len}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              p: 1,
              borderBottom: "0.1rem solid var(--gray-200)",
              background: "var(--neutral-100)",
              "&:hover": {
                backgroundColor: "var(--soft-peach)",
              },
            }}
          >
            <Typography
              component={"p"}
              sx={{
                color: "var(--gray-400)",
                fontSize: "1.2rem",
                fontWeight: 400,
              }}
            >
              {capitalizeFirstLetter(countryName)}
            </Typography>
            <Typography
              component={"p"}
              sx={{
                color: "var(--black-900)",
                fontSize: "1.4rem",
                fontWeight: 400,
              }}
            >
              {countryCodeList[countryName]?.code}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectCountry;
